import { Fragment, useEffect, useState } from "react";
import { TestIdClassNameProps } from "@app/types/Common";
import type { Element, EmbedCode, Section, seeAlso, Tag } from "@app/types/Cue";
import { AdvertisementTypeEnum, Name } from "@app/types/enums";
import { ExternalLink, Note, Text } from "@blocks/Blocks";
import Advertisement from "@components/Advertisement/Advertisement";
import CASBlock from "@components/CASBlock";
import ArticleSeeAlso from "@pages/Article/components/ArticleSeeAlso";
import { BrightcoveVideo } from "@pages/Article/components/StoryElements/BrightcoveVideo";
import {
  calculateLengthToTruncateBeforeDisplayingPaywall,
  calculateTotalContentLength,
} from "@pages/Article/utils/ArticleUtility";
import useOKTAUserStore, {
  OKTAUserTypeEnum,
} from "@src/app/store/useOKTAUserStore";
import { CDP_PAYWALL_MODULE_ID } from "@util/constant";
import { getFieldValue, getListTag, GoogleAdsSlotFactory } from "@util/helpers";
import axios from "axios";
import cx from "classnames";

import ArticleNewsletter from "./ArticleNewsletter/ArticleNewsletter";
import ArticleInlineImage from "./StoryElements/ArticleInlineImage";
import Box from "./StoryElements/Box";
import DataWrapper from "./StoryElements/DataWrapper";
import DynamicIframeWrapper from "./StoryElements/DynamicIframe/DynamicIframeWrapper";
import Embed from "./StoryElements/Embed";
import OmnyPodcast from "./StoryElements/OmnyPodcast";
import PodcastEmbed from "./StoryElements/PodcastEmbed";
import PullQuote from "./StoryElements/PullQuote";
import SubHeading from "./StoryElements/SubHeading";
import Table from "./StoryElements/Table";
import YouTubeVideo from "./StoryElements/YouTubeVideo";

type ArticleParagraphsProps = TestIdClassNameProps & {
  id: string;
  content: Element[];
  seeAlso?: seeAlso;
  embedCodes?: EmbedCode[];
  displayPaywall: boolean;
  displayRelatedLinksAndNewsletterSignupForm?: boolean;
  keywords?: Tag[];
  sections?: Section[];
  index?: number;
};

export default function ArticleParagraphs({
  id,
  testId,
  rootClassName,
  content = [],
  seeAlso,
  displayPaywall = false,
  displayRelatedLinksAndNewsletterSignupForm = false,
  keywords = [],
  sections = [],
  index,
}: ArticleParagraphsProps): React.ReactElement | null {
  const [userType] = useOKTAUserStore((store) => [store.userInfo?.usertype]);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [articleElements, setArticleElements] = useState(content);
  useEffect(() => {
    const fetchArticleElements = async (id: string) => {
      const url = "/_plat/api/v1/article-elements";
      const { data } = await axios.post(url, { id });
      setArticleElements(data);
    };

    if (userType === OKTAUserTypeEnum.SUBSCRIBER) {
      setIsSubscriber(true);
      fetchArticleElements(id);
    }
  }, [userType, id]);

  const mainSection = sections.at(0);

  let numParagraphs = 0;
  let totalContentLength = 0;
  let truncatedLength = 0;
  let cumulativeContentLength = 0;
  let paywallDisplayed = false;

  if (displayPaywall && !isSubscriber) {
    totalContentLength = calculateTotalContentLength(articleElements);
    truncatedLength =
      calculateLengthToTruncateBeforeDisplayingPaywall(totalContentLength);
  }

  const idsToRemove = articleElements
    .flatMap((element) => {
      if (!element.children) return;
      return element.children.map((id) => id);
    })
    .filter((element) => element !== undefined);

  const elements = articleElements.filter(
    (element) => !idsToRemove.includes(element.id)
  );

  return (
    <div
      className={cx(
        rootClassName,
        "body-content mb-6 mt-4 font-light text-gray-850"
      )}
      data-testid={testId}
    >
      {elements.map((element) => {
        if (paywallDisplayed) {
          return null; // Skip remaining elements once paywall is displayed
        }

        switch (element.type) {
          case "sub_head":
            return <SubHeading key={element.id} fields={element.fields} />;

          case "paragraph": {
            if (!element.fields?.[0]?.value) break; // Edge case where the paragraph element doesn't have any paragraph information

            numParagraphs++;
            const paragraphText = element.fields?.[0].value;

            if (displayPaywall && !isSubscriber) {
              // Calculate cumulative length
              cumulativeContentLength += [...paragraphText].length;
              // Check if cumulative length exceeds truncated length
              if (cumulativeContentLength > truncatedLength) {
                // Truncate the text
                const truncatePosition =
                  cumulativeContentLength - truncatedLength;
                const chars = [...paragraphText];
                const truncatedText =
                  chars.slice(0, truncatePosition).join("") + "…";

                paywallDisplayed = true; // Mark paywall as displayed
                // Process the truncated text and paywall component
                return (
                  <Fragment key={element.id}>
                    <Text
                      text={truncatedText}
                      annotations={element.fields?.[0].annotations}
                      annotationContext={articleElements.filter((blocks) =>
                        element.fields?.[0]?.annotations
                          ?.map((a) => a.value)
                          .includes(blocks.id)
                      )}
                    />

                    <CASBlock cdpId={CDP_PAYWALL_MODULE_ID} index={index} />
                  </Fragment>
                );
              }
            }

            // Process the text normally
            return (
              <Fragment key={element.id}>
                <Text
                  text={paragraphText}
                  annotations={element.fields?.[0].annotations}
                  annotationContext={articleElements.filter((blocks) =>
                    element.fields?.[0]?.annotations
                      ?.map((a) => a.value)
                      .includes(blocks.id)
                  )}
                />

                {numParagraphs === 3 && mainSection ? (
                  <Advertisement
                    adUnitProps={{
                      id: index !== 0 ? `dfp-ad-imu1-${index}` : `dfp-ad-imu1`,
                      type: AdvertisementTypeEnum.IMU1,
                      slot: GoogleAdsSlotFactory.imu1(mainSection.uniqueName),
                      isRefresh: index !== 0,
                    }}
                    rootClassName="mb-6"
                  />
                ) : null}

                {numParagraphs === 6 && (
                  // Insert NewsletterSignup and Feedback components after the 6th paragraph.
                  <>
                    {displayRelatedLinksAndNewsletterSignupForm &&
                    seeAlso?.title ? (
                      <ArticleSeeAlso
                        title={seeAlso.title}
                        articleId={seeAlso.articleId}
                        slug={seeAlso.slug}
                      />
                    ) : null}

                    {displayRelatedLinksAndNewsletterSignupForm ? (
                      <div className="py-10">
                        <ArticleNewsletter
                          rootClassName="mb-6"
                          sections={sections}
                          tags={keywords}
                        />
                      </div>
                    ) : null}
                  </>
                )}
              </Fragment>
            );
          }

          case "podcast_wrapper":
            return (
              <PodcastEmbed
                key={element.id}
                fields={element.fields}
                rootClassName="mb-6"
                annotation={element.type}
              />
            );

          case "image":
            return (
              <Fragment key={element.id}>
                {element.relation ? (
                  <ArticleInlineImage
                    fields={element.fields}
                    relation={element.relation}
                    annotation={element.type}
                    rootClassName="mb-6"
                  />
                ) : null}
              </Fragment>
            );

          case "brightcove_video":
            return (
              <BrightcoveVideo
                key={element.id}
                fields={element.fields}
                annotation={element.type}
                rootClassName="mb-6"
              />
            );

          case "data_wrapper":
            return (
              <DataWrapper
                key={element.id}
                fields={element.fields}
                annotation={element.type}
                rootClassName="mb-6"
              />
            );
          case "dynamic_iframe_wrapper":
            return (
              <DynamicIframeWrapper
                key={element.id}
                fields={element.fields}
                annotation={element.type}
                rootClassName="mb-6"
              />
            );

          case "graphic":
            return (
              <iframe
                key={element.id}
                src={element.relation?.href}
                style={{ width: "100%", minHeight: 300 }}
                title={element.id}
              />
            );

          case "note":
            return (
              <Note
                key={element.id}
                value={element?.fields?.map((f) => f.value).join("\n")}
              ></Note>
            );

          case "grouped_relation":
            return (
              <ExternalLink
                key={element.id}
                // @ts-expect-error incomplete typedef
                src={element.children.map((group: unknown) => {
                  const rel = articleElements?.find?.((el) => el.id === group);

                  return rel?.relation?.id;
                })}
              />
            );

          case "embed":
            return (
              <Embed
                key={element.id}
                fields={element.fields}
                rootClassName="mb-6"
                annotation={element.type}
              />
            );

          case "list_bulleted":
          case "list_numbered": {
            const ListTag = getListTag(element.type);
            return (
              <ListTag
                key={element.id}
                data-annotation={element.type}
                className={cx("mb-6 pl-8", {
                  "list-disc": ListTag === "ul",
                  "list-decimal": ListTag === "ol",
                })}
              >
                {element.children
                  ?.flatMap((id) => {
                    return articleElements.filter(
                      (_element) => _element.id === id
                    );
                  })
                  .map((item) => {
                    return (
                      <li key={item.id}>
                        {getFieldValue(item.fields, Name.Paragraph) as string}
                      </li>
                    );
                  })}
              </ListTag>
            );
          }

          case "pull_quote":
            return (
              <PullQuote
                key={element.id}
                annotation={element.type}
                fields={element.fields}
                rootClassName="mb-6"
              />
            );

          case "table":
            return (
              <Table
                key={element.id}
                fields={element.fields}
                annotation={element.type}
              />
            );

          case "horizontal_row":
            return <hr className="mb-6" data-annotation="horizontal_row" />;

          case "youtube_video":
            return (
              <YouTubeVideo
                key={element.id}
                fields={element.fields}
                annotation={element.type}
                rootClassName="mb-6"
              />
            );

          case "omny_podcast":
            return (
              <OmnyPodcast
                key={element.id}
                fields={element.fields}
                annotation={element.type}
                rootClassName="mb-6"
              />
            );

          case "box":
            return (
              <Box
                key={element.id}
                box={
                  element.children?.flatMap((id) =>
                    content.filter((_element) => _element.id === id)
                  ) || []
                }
                content={content}
                data-annotation={element.type}
                rootClassName="mb-6"
              />
            );

          case "headline":
          case "kicker":
          case "standfirst":
          case "slug":
          case "external_link":
          case "map":
          case "internal_link":
          case "endnote":
          case "relation":
          case "stock_picker": // Stock codes are used as annotations
            return null;
        }

        return (
          <div
            key={element.id}
            className="border-accent my-m box-border max-h-[300px] w-full overflow-auto border p-5"
            style={{ fontSize: 12 }}
          >
            <code>Unsupported element {element.type as React.ReactNode}</code>
            <pre>{JSON.stringify(element, null, 2)}</pre>
          </div>
        );
      })}

      {numParagraphs < 6 && (
        // Append Newsletter and Feedback components at the end if there are less than 6 paragraphs
        <>
          {displayRelatedLinksAndNewsletterSignupForm && seeAlso?.title ? (
            <ArticleSeeAlso
              title={seeAlso.title}
              articleId={seeAlso.articleId}
              slug={seeAlso.slug}
            />
          ) : null}

          {displayRelatedLinksAndNewsletterSignupForm ? (
            <div className="py-10">
              <ArticleNewsletter
                rootClassName="mb-6"
                sections={sections}
                tags={keywords}
              />
            </div>
          ) : null}
        </>
      )}
      {!displayPaywall && (
        <div style={{ display: "none" }} data-testid="no-paywall" />
      )}
    </div>
  );
}
